import { Eye, User, Ban, CheckCircle } from "lucide-react";
import { ContainerCard, EmployeeDetails, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { useState } from "react";
import { EMPLOYEE_PERSONAL_INFO } from "../../constants/api";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import dayjs from "dayjs";
import { cn } from "../../utils/tailwindUtils";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import useApiMethods from "../../lib/useApiMethods";

const EmployeeList = () => {
    // states and variables
    const { update } = useApiMethods()
    const [employeeData, setEmployeeData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loading, setLoading] = useState(false);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [actionType, setActionType] = useState(''); // 'activate' or 'deactivate'

    const { edit: editPermission, add: addPermission, active: isActive } = checkUserPermissionFunc('employee', 'employees', 'SubMenu');
    let navigate = useNavigate();

    // columns
    const employeeColumns = [
        {
            accessor: "employee_name",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            width: "250px",
            Cell: ({ row }) => {
                const record = row.original || row;

                const empDetails = {
                    ...record,
                    profile: record?.profile?.length > 0 && record.profile[0] ? record.profile[0] : "",
                    designation_id: record?.designation_id?.length > 0
                        ? { ...record.designation_id[0] }
                        : { id: 0, designation_name: "" },
                    id: record?.employee_id || ""
                };

                return <EmployeeDetails details={empDetails} size="xs" />;
            },
        },
        {
            accessor: "department",
            Header: "Department",
            sortable: true,
            filterable: true,
            width: "70px",
            Cell: ({ row }) => {
                const record = row.original || row;
                const department = record?.department_id?.[0]?.department_name || "-";
                return department;
            },
        },
        {
            accessor: "designation",
            Header: "Designation",
            sortable: true,
            filterable: true,
            width: "70px",
            Cell: ({ row }) => {
                const record = row.original || row;
                const designation = record?.designation_id?.[0]?.designation_name || "-";
                return designation;
            },
        },
        {
            accessor: "reporting_manager_id",
            Header: "Reporting Manager",
            sortable: true,
            filterable: true,
            width: "70px",
            Cell: ({ row }) => {
                const record = row.original || row;

                if (!record?.reporting_manager_id?.length) {
                    return "-";
                }

                const manager = record.reporting_manager_id[0];
                const firstName = manager?.first_name || "";
                const lastName = manager?.last_name || "";

                return `${firstName} ${lastName}`.trim() || "-";
            },
        },
        {
            accessor: "is_active",
            Header: "Status",
            sortable: false,
            filterable: true,
            Cell: ({ value }) => {
                const isActive = Boolean(value);

                return (
                    <span
                        className={cn(
                            "px-2 py-1 text-xs font-medium rounded-full capitalize",
                            isActive
                                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                        )}
                    >
                        {isActive ? "Active" : "Inactive"}
                    </span>
                );
            },
        },
        {
            accessor: "date_of_joining",
            Header: "Date of Joining",
            sortable: true,
            filterable: true,
            filterType: 'date',
            width: "70px",
            Cell: ({ value }) => {
                if (!value) return "-";

                try {
                    return dayjs(value, "YYYY-MM-DD").format("DD-MM-YYYY");
                } catch (error) {
                    console.error("Date formatting error:", error);
                    return value;
                }
            }
        },

        ...(editPermission ? [
            {
                accessor: 'actions',
                Header: 'Actions',
                fixed: true,
                align: 'right',
                Cell: ({ row }) => {
                    const record = row.original || row;
                    const isEmployeeActive = Boolean(record.is_active);

                    const handleView = () => {
                        navigate("/employee/viewDetails", {
                            state: {
                                row: record,
                                employeeID: record.id || record.employee_id,
                                isActive: isActive
                            }
                        });
                    };

                    const handleToggleStatus = () => {
                        setSelectedRow(record);
                        setActionType(isEmployeeActive ? 'deactivate' : 'activate');
                        setIsConfirmOpen(true);
                    };

                    return (
                        <div className="flex gap-1 justify-end">
                            <Button
                                variant='ghost'
                                onClick={handleView}
                                className="px-3 py-2 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                                title="View Employee Details"
                            >
                                <Eye className="text-orange-500 h-4 w-4" />
                            </Button>
                            {/* <Button
                                variant='ghost'
                                onClick={handleToggleStatus}
                                className={cn(
                                    "px-3 py-2",
                                    !isEmployeeActive
                                        ? "hover:bg-red-50 dark:hover:bg-red-900/20"
                                        : "hover:bg-green-50 dark:hover:bg-green-900/20"
                                )}
                                title={isEmployeeActive ? "Deactivate Employee" : "Activate Employee"}
                            >
                                {!isEmployeeActive ? (
                                    <Ban className="text-red-500 h-4 w-4" />
                                ) : (
                                    <CheckCircle className="text-green-500 h-4 w-4" />
                                )}
                            </Button> */}
                        </div>
                    );
                }
            }
        ] : [])
    ];

    const handleAddEmployee = () => {
        navigate('/employee/add');
    };

    const handleConfirmToggle = async () => {
        if (!selectedRow) return;

        setLoading(true);
        try {
            const employeeId = selectedRow.id;
            const newStatus = actionType === 'activate';

            const payload = {
                employee_id: employeeId,
                is_active: newStatus,  
            };

            await update(EMPLOYEE_PERSONAL_INFO, payload);

            // Update local state immediately for better UX
            setEmployeeData(prevData =>
                prevData.map(employee =>
                    (employee.employee_id === employeeId || employee.id === employeeId)
                        ? { ...employee, is_active: newStatus }
                        : employee
                )
            );

            setServerTrigger(true)

        } catch (error) {
            console.error(`Error ${actionType}ing employee:`, error);
        } finally {
            setLoading(false);
            setIsConfirmOpen(false);
            setSelectedRow(null);
            setActionType('');
            setServerTrigger(false)
        }
    };

    const getModalConfig = () => {
        const isActivating = actionType === 'activate';
        return {
            title: isActivating ? "Activate Employee" : "Deactivate Employee",
            description: isActivating
                ? "Are you sure you want to activate this employee? They will regain access to the system."
                : "Are you sure you want to deactivate this employee? They will no longer have access to the system.",
            confirmlabel: isActivating ? "Activate" : "Deactivate",
            type: isActivating ? "success" : "warning"
        };
    };

    const modalConfig = getModalConfig();

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={User}
                title={'Employee'}
                subtitle={'Manage Employee List and there details'}
                buttonEnable={!!addPermission}
                buttonName="Add Employee"
                totalRecords={totalRecords}
                setOpenDialog={handleAddEmployee}
            />

            <Reactable
                data={employeeData}
                setData={setEmployeeData}
                columns={employeeColumns}
                serverSide
                url={EMPLOYEE_PERSONAL_INFO}
                setTotalCount={setTotalRecords}
                serverTrigger={serverTrigger}
                tableHeight="calc(100vh - 320px)"
                rowSize={editPermission ? "sm" : null}
                isCSVRequired
                isPDFRequired
            />

            <ConfirmPopupModal
                open={isConfirmOpen}
                onClose={() => {
                    setIsConfirmOpen(false);
                    setSelectedRow(null);
                    setActionType('');
                }}
                onConfirm={handleConfirmToggle}
                title={modalConfig.title}
                description={modalConfig.description}
                type={modalConfig.type}
                confirmlabel={modalConfig.confirmlabel}
                destructive={actionType === 'deactivate'}
                loading={loading}
            />
        </ContainerCard>
    );
};

export default EmployeeList;